import {
  Box,
  Divider,
  Modal,
  ModalContent,
  ModalOverlay
} from '@chakra-ui/react';
import React, { FC } from 'react';
import { useToggle } from 'react-use';
import { AddLiquiditySectionTitle } from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquiditySectionTitle';

import { SubmitButton } from '@app/components/SubmitButton';
import { formatNumber } from '@app/helpers/format';
import { Web3Button } from '@thirdweb-dev/react';
import clsx from 'clsx';
import s from '@app/screens/airdrop/ClaimsList/ClaimsList.module.scss';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import { InfoRow } from '@app/screens/liquidity/AddLiquidityV3/components/InfoRow';
import { useConfig } from '@app/config';
import { TokensTitle } from '@app/screens/liquidity/AddLiquidityV3/components/TokensTitle';
import { useFarmBaseApr } from '@app/hooks/plugins/useFarmBaseApr';
import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';
import { IDerivedLiquidityByGammaInfo } from '@app/screens/liquidity/LiquidityWizard/hooks/useDerivedGammaMintInfo';
import { AddLiquidityWithProviderButtonETH } from '@app/screens/liquidity/AddLiquidityV3/components/gamma/AddLiquidityWithProviderButtonETH';
import AddLiquidityWithProviderButton from '@app/screens/liquidity/AddLiquidityV3/components/gamma/AddLiquidityWithProviderButton';

interface Props {
  mintInfo: IDerivedLiquidityByGammaInfo;
}

export const CreatePositionWithKpNft: FC<Props> = ({ mintInfo }) => {
  const config = useConfig();
  const [open, toggleOpen] = useToggle(false);

  const {
    currencyA,
    currencyB,
    actions: { setStep }
  } = useLiquidityWizardState();

  const { farmBaseApr, lockBonusApr, totalApr } = useFarmBaseApr(
    mintInfo.nftPoolAddress,
    mintInfo.vault?.tvl,
    mintInfo.vault?.apr,
    mintInfo.lockDuration
  );

  if (!mintInfo.token0 || !mintInfo.token1) {
    return null;
  }

  const isReady =
    mintInfo.parsedAmounts.CURRENCY_A?.greaterThan(0) &&
    mintInfo.parsedAmounts.CURRENCY_B?.greaterThan(0);

  return (
    <>
      <SubmitButton
        isLoading={false}
        label={mintInfo.errorMessage ?? 'Create position'}
        onClick={toggleOpen}
        disabled={!isReady || !!mintInfo.errorMessage}
      />
      <Modal
        isOpen={open}
        onClose={() => {
          toggleOpen(false);
        }}
        isCentered
        motionPreset="slideInBottom"
        size="xs"
      >
        <ModalOverlay />
        <ModalContent
          sx={{ maxWidth: ['100vw', '586px'], padding: ['0', '24px'] }}
        >
          <AddLiquiditySectionTitle
            isCompleted={false}
            label="Create position"
            hideStatus
          />
          <Divider
            sx={{
              width: '100%',
              background: 'neutral.300',
              height: '1px',
              my: 3
            }}
          />

          <TokensTitle
            token0Symbol={mintInfo.token0.symbol ?? ''}
            token1Symbol={mintInfo.token1.symbol ?? ''}
            address={mintInfo.vault?.id ?? ''}
          />

          {/*<LockSection mintInfo={mintInfo} />*/}

          <AddLiquiditySectionTitle
            isCompleted={false}
            label="Estimates"
            hideStatus
          />

          <Box p="0 14px">
            <InfoRow
              label="Total APR"
              value={`${totalApr ? formatNumber(totalApr, 2) : 0}%`}
            />
            <InfoRow
              label="Fee APR"
              value={`${mintInfo.vault ? mintInfo.vault.apr.toFixed(2) : 0}%`}
            />
            <InfoRow label="Farm Base APR" value={`${farmBaseApr ?? 0}%`} />
            <InfoRow label="Lock Bonus APR" value={`${lockBonusApr ?? 0}%`} />
          </Box>

          {mintInfo.nftPoolAddress === null ? (
            <>
              <Alert
                status="warning"
                display="flex"
                border="1px solid"
                borderColor="neutral.700"
                my="16px"
                borderRadius="10px"
                py="10px"
                px="12px"
              >
                <AlertIcon w="16px" h="16px" mr="8px" color="neutral.400" />
                <AlertDescription color="neutral.400">
                  You are the very first kpNFT minter for this asset! You will
                  need to initialize kpNFT contract first.
                </AlertDescription>
              </Alert>

              <Web3Button
                className={clsx(s.claimBtn, s.outlined)}
                contractAddress={config?.CONTRACTS.NFT_POOL_FACTORY!}
                action={async contract =>
                  contract.call('createPool', [mintInfo.vault?.id])
                }
                onError={error => {
                  console.error(error);
                  showErrorToast('Failed to initialize pool');
                }}
                onSuccess={async () => {
                  showSuccessToast('Successfully initialized NFT pool');
                  await mintInfo.getPoolAddress();
                }}
                isDisabled={false}
              >
                Initialize NFT pool
              </Web3Button>
            </>
          ) : (
            <>
              {currencyA?.isNative || currencyB?.isNative ? (
                <AddLiquidityWithProviderButtonETH
                  baseCurrency={currencyA}
                  quoteCurrency={currencyB}
                  mintInfo={mintInfo}
                  poolAddress={mintInfo.vault?.id}
                  onComplete={() => {
                    toggleOpen(false);

                    setStep(2);
                  }}
                />
              ) : (
                <AddLiquidityWithProviderButton
                  baseCurrency={currencyA}
                  quoteCurrency={currencyB}
                  mintInfo={mintInfo}
                  poolAddress={mintInfo.vault?.id}
                  onComplete={() => {
                    toggleOpen(false);

                    setStep(2);
                  }}
                />
              )}
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
